import type { GscDimension, GscSearchType } from './client'

export const GSC_SEARCH_TYPES = ['web', 'image', 'video', 'news', 'discover', 'googleNews'] as const

export const DIMENSION_SET_KEYS = [
  'totals',
  'query',
  'page',
  'geo',
  'appearance',
  'query-page',
] as const

export type GscDimensionSet = (typeof DIMENSION_SET_KEYS)[number]

export interface DimensionSetSpec {
  key: GscDimensionSet
  dimensions: GscDimension[]
  /**
   * 'range' pulls the whole backfill in one paged request; 'day' pulls one day
   * per job. Only the totals series is small enough for a range — 16 months of
   * daily totals is under 500 rows, while a day of queries can be tens of
   * thousands. Per-day jobs also mean a failure retries a day rather than a
   * year, and progress is visible while the backfill runs.
   */
  granularity: 'range' | 'day'
  /** Discover and Google News expose no query dimension, and searchAppearance
   * is web-only. Asking anyway is a 400, not an empty result. */
  searchTypes: readonly GscSearchType[]
}

const ALL_TYPES = GSC_SEARCH_TYPES
const PAGE_TYPES = ['web', 'image', 'video', 'news', 'discover', 'googleNews'] as const
const QUERY_TYPES = ['web', 'image', 'video', 'news'] as const

/**
 * `date` leads every dimension list so row keys parse uniformly, even on per-day
 * requests where it is redundant — with the single exception of `appearance`,
 * which Google refuses to group alongside anything else. Use `leadsWithDate` to
 * decide whether a row's date comes from its keys or from the window.
 */
export const DIMENSION_SETS: Record<GscDimensionSet, DimensionSetSpec> = {
  totals: {
    key: 'totals',
    dimensions: ['date'],
    granularity: 'range',
    searchTypes: ALL_TYPES,
  },
  query: {
    key: 'query',
    dimensions: ['date', 'query'],
    granularity: 'day',
    searchTypes: QUERY_TYPES,
  },
  page: {
    key: 'page',
    dimensions: ['date', 'page'],
    granularity: 'day',
    searchTypes: PAGE_TYPES,
  },
  geo: {
    key: 'geo',
    dimensions: ['date', 'country', 'device'],
    granularity: 'day',
    searchTypes: ALL_TYPES,
  },
  appearance: {
    key: 'appearance',
    // The one series that cannot include `date`: Google answers "cannot group by
    // search appearance dimension together with another dimension" and fails the
    // whole request. Windows are a single day anyway, so the date is supplied by
    // the caller instead of read back off the row.
    dimensions: ['searchAppearance'],
    granularity: 'day',
    searchTypes: ['web'],
  },
  'query-page': {
    key: 'query-page',
    dimensions: ['date', 'query', 'page'],
    granularity: 'day',
    searchTypes: QUERY_TYPES,
  },
}

export function dimensionSetsFor(searchType: GscSearchType): DimensionSetSpec[] {
  return DIMENSION_SET_KEYS.map((key) => DIMENSION_SETS[key]).filter((spec) =>
    spec.searchTypes.includes(searchType),
  )
}

export const GSC_RETENTION_MONTHS = 16

/** Google finalises a day roughly three days after the fact. Pulling anything
 * newer with dataState 'final' returns nothing. */
export const GSC_FINAL_LAG_DAYS = 3

/** Days re-pulled on every daily sync. Rows keep moving for a few days after
 * they first appear, and upserting the overlap is cheaper than being wrong. */
export const GSC_REPULL_DAYS = 5

export function toIsoDate(value: Date): string {
  return value.toISOString().slice(0, 10)
}

export function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return toIsoDate(date)
}

/** Newest day Google will report as final. */
export function latestFinalDate(today: Date): string {
  return addDays(toIsoDate(today), -GSC_FINAL_LAG_DAYS)
}

/**
 * The oldest day in the trailing stretch our copy may still be revised over.
 *
 * Anchored on the newest day we actually hold rather than on today, so a series
 * that is behind still marks the right days: they are exactly the ones the next
 * sync re-reads and overwrites. Anchoring on today would mark nothing at all on
 * a stale series, which is the one case where the reader most needs to know the
 * end of the line is soft.
 */
export function provisionalFrom(latest: string): string {
  return addDays(latest, -(GSC_REPULL_DAYS - 1))
}

/** Oldest day that still exists. Everything before this is gone for good, which
 * is what bounds the backfill. */
export function retentionHorizon(today: Date): string {
  const date = new Date(today)
  date.setUTCMonth(date.getUTCMonth() - GSC_RETENTION_MONTHS)
  return toIsoDate(date)
}

/**
 * Backfill chunks, newest first.
 *
 * Ordering matters more than it looks: the last 30 days landing in the first
 * minute is what makes a freshly connected site feel alive, while 2025 is still
 * being fetched behind it.
 */
export function backfillDatesDesc(today: Date, until?: string): string[] {
  const newest = latestFinalDate(today)
  const oldest = until ?? retentionHorizon(today)
  const dates: string[] = []
  for (let cursor = newest; cursor >= oldest; cursor = addDays(cursor, -1)) {
    dates.push(cursor)
  }
  return dates
}

/** False only for `appearance`, whose rows carry no date of their own. */
export function leadsWithDate(spec: DimensionSetSpec): boolean {
  return spec.dimensions[0] === 'date'
}
